import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flame, Mountain, Wind, Droplets } from "lucide-react";

const marsData = [
  {
    key: "fire",
    label: "Fire Mars",
    icon: Flame,
    strengths:
      "Enthusiasm, confidence, initiative, and courage. Acts with natural spark and inspires forward motion.",
    challenges:
      "Impatience, impulsivity, or burnout if not balanced with restoration.",
  },
  {
    key: "earth",
    label: "Earth Mars",
    icon: Mountain,
    strengths:
      "Steadiness, discipline, practicality, and perseverance. Builds slow, lasting momentum.",
    challenges:
      "Rigidity, resistance to change, or becoming overly focused on productivity.",
  },
  {
    key: "air",
    label: "Air Mars",
    icon: Wind,
    strengths:
      "Motivated by ideas, learning, communication, and creativity. Naturally curious and connective.",
    challenges:
      "Scattered focus, overthinking decisions, or difficulty translating ideas into action.",
  },
  {
    key: "water",
    label: "Water Mars",
    icon: Droplets,
    strengths:
      "Acts through intuition, emotion, compassion, and deep feeling. Sensitive to the currents of others.",
    challenges:
      "Hesitation, indirectness, emotional reactivity, or difficulty expressing anger or asserting boundaries.",
  },
];

const AstrologySection = () => {
  return (
    <section
      data-testid="astrology-section"
      className="bg-[#F4F1ED] py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76] mb-6">
            Optional · Astrology
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#2D3A2B] leading-tight">
            A tool for <span className="italic">self-reflection</span>, never for
            labels or predictions.
          </h2>
          <p className="mt-8 text-lg text-[#4A5D46] leading-relaxed font-light">
            Whether we explore these patterns through Yoga Therapy, lifestyle,
            self-inquiry, or—if it resonates with you—astrology, the intention is
            never to judge or define you. Rather, it is to cultivate greater
            awareness of your natural tendencies, helping you recognize where you
            are already in harmony and where you may benefit from practices that
            restore greater balance, resilience, and wellbeing.
          </p>
        </div>

        <div className="bg-[#FDFCFB] border border-[#E8E5DF] rounded-md p-6 md:p-10">
          <p className="text-sm md:text-base text-[#4A5D46] mb-8 leading-relaxed font-light max-w-3xl">
            If astrology resonates with you, Marissa can incorporate your natal
            chart as one of several optional tools. For example, we might
            explore your <span className="italic">Mars placement</span> — a
            symbolic lens on drive, motivation, vitality, courage, and how you
            respond to challenge.
          </p>
          <Tabs defaultValue="fire" className="w-full">
            <TabsList
              data-testid="astrology-tabs"
              className="bg-[#F4F1ED] p-1 rounded-full flex flex-wrap gap-1 h-auto w-full md:w-auto"
            >
              {marsData.map((m) => (
                <TabsTrigger
                  key={m.key}
                  value={m.key}
                  data-testid={`astrology-tab-${m.key}`}
                  className="rounded-full data-[state=active]:bg-[#8A9A83] data-[state=active]:text-white text-[#4A5D46] px-6 py-2 flex items-center gap-2"
                >
                  <m.icon className="w-4 h-4" aria-hidden />
                  <span>{m.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {marsData.map((m) => (
              <TabsContent
                key={m.key}
                value={m.key}
                className="mt-10 grid md:grid-cols-2 gap-10"
              >
                <div>
                  <p className="text-xs tracking-[0.3em] uppercase text-[#7B8E76] mb-3">
                    Natural Strengths
                  </p>
                  <p className="font-serif text-2xl text-[#2D3A2B] leading-snug">
                    {m.strengths}
                  </p>
                </div>
                <div>
                  <p className="text-xs tracking-[0.3em] uppercase text-[#7B8E76] mb-3">
                    Potential Challenges
                  </p>
                  <p className="font-serif text-2xl text-[#4A5D46] leading-snug italic">
                    {m.challenges}
                  </p>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          <p className="mt-12 text-sm text-[#7B8E76] italic leading-relaxed max-w-3xl">
            We can also look at the house where Mars is placed — a symbolic map
            of the life areas (relationships, career, family, creativity,
            spirituality, finances, learning) where this energy naturally seeks
            expression. These explorations are not used to label, predict, or
            diagnose you. Rather, they provide a thoughtful framework for
            self-reflection.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AstrologySection;
